import React, { useContext, useEffect, useRef } from 'react'
import { AppContext } from '../AppContext';
import { format12 } from '../scripts/SmartAzanClock'
import { ThemePalettes } from '../data/DisplayThemes';

export default function Clock() {

    const { showMenu, setShowMenu, nextText, todaysDate, hijriDate, locationSettings,
        calculationSettings, deviceSettings, hourAngle, vakits, arcVakits, displayTime, currentVakit, nextVakit, currentArcVakit,
        elapsed, background, dim, clockOpacity, midnightTime, twoThirdTime, midnightAngle, oneThirdAngle, twoThirdAngle, alarmSettings, naflAlarmSettings, isWeekDay,
        language, isRtl, t } = useContext(AppContext)
    const canvasRef = useRef(null)
    const driftRef = useRef(null)
    const dragWrapperRef = useRef(null)
    const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0, hasMoved: false })

    const handlePointerDown = (e) => {
        const drag = dragRef.current
        drag.isDragging = true
        drag.startX = e.clientX - drag.offsetX
        drag.startY = e.clientY - drag.offsetY
        drag.hasMoved = false
        e.currentTarget.setPointerCapture(e.pointerId)
    }

    const handlePointerMove = (e) => {
        const drag = dragRef.current
        if (!drag.isDragging) return
        const newX = e.clientX - drag.startX
        const newY = e.clientY - drag.startY
        if (Math.abs(newX - drag.offsetX) > 3 || Math.abs(newY - drag.offsetY) > 3)
            drag.hasMoved = true
        drag.offsetX = newX
        drag.offsetY = newY
        const el = dragWrapperRef.current
        if (el) el.style.transform = `translate(${newX}px, ${newY}px)`
    }

    const handlePointerUp = () => {
        const drag = dragRef.current
        drag.isDragging = false
        drag.offsetX = 0
        drag.offsetY = 0
        const el = dragWrapperRef.current
        if (el) el.style.transform = 'translate(0px, 0px)'
    }

    const handleClick = () => {
        if (!dragRef.current.hasMoved) {
            setShowMenu(!showMenu)
        }
    }

    const handleDashboardKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowMenu(!showMenu);
        }
    }
    const size = 1000; /* size = width = height */
    const gray = '#4B4E54';
    const silver = 'silver';
    const theme = ThemePalettes[deviceSettings.theme] || ThemePalettes.classic;
    const faceColor = theme.face;
    const textColor = theme.text;
    const handColor = theme.hand;
    const handAccentColor = theme.handAccent;

    useEffect(() => {
        const el = driftRef.current
        if (!el) return
        if (deviceSettings.screenSaver !== 'Y') {
            el.style.transform = 'translate(0px, 0px)'
            document.body.style.backgroundPosition = '50% 50%'
            return
        }
        const speed = 0.3 // pixels per frame
        let x = 0, y = 0, dx = speed, dy = speed * 0.7
        const interval = setInterval(() => {
            const canvas = canvasRef.current
            if (!canvas) return
            const rect = canvas.getBoundingClientRect()
            const zoomed = deviceSettings.zoomedIn === 'Y'
            const diffX = Math.abs(window.innerWidth - rect.width) / 2
            const diffY = Math.abs(window.innerHeight - rect.height) / 2
            const maxX = zoomed ? 30 : Math.max(diffX, 30)
            const maxY = zoomed ? 20 : Math.max(diffY, 30)
            x += dx
            y += dy
            if (x >= maxX || x <= -maxX) dx = -dx
            if (y >= maxY || y <= -maxY) dy = -dy
            x = Math.max(-maxX, Math.min(maxX, x))
            y = Math.max(-maxY, Math.min(maxY, y))
            el.style.transform = `translate(${x}px, ${y}px)`
            if (background.length > 0)
                document.body.style.backgroundPosition = `${50 + x * 0.15}% ${50 + y * 0.15}%`
        }, 50)
        return () => clearInterval(interval)
    }, [deviceSettings.screenSaver, deviceSettings.zoomedIn])

    useEffect(() => {

        if (deviceSettings.layout === 'dashboard') {
            updateBackground('');
            return;
        }

        if (!canvasRef.current) {
            return;
        }

        const ctx = (canvasRef.current).getContext("2d")
        const currentName = prayerName(currentVakit.name);
        const nextName = prayerName(nextVakit.name);
        const currentArcName = prayerName(currentArcVakit.name);
        const clockDate = getClockDate();
        const clockHijriDate = getClockHijriDate();
        const statusText = isRtl
            ? `${t('clock.elapsed')} ${elapsed} · ${t('clock.remainingUntil')} ${nextName}`
            : `${t('clock.elapsed')} ${elapsed} · ${nextName} ${t('clock.in')}`;

        updateBackground(background);

        sac.clearCanvas(ctx)
            .fillCircle(ctx, 490, 0, 0, theme.faceGlow, theme.glowOpacity)
            .fillCircle(ctx, 488, 0, 0, faceColor, theme.faceOpacity)
            .drawNumbers24(ctx, 455, 13, textColor)
            .drawArcs(ctx, 421, 41)
            .drawHand(ctx, midnightAngle, 413, 443, 3.5, handColor)
            .printAt(ctx, '1/2', 14, textColor, 403, midnightAngle)
            .drawHand(ctx, oneThirdAngle, 413, 443, 3.5, handColor)
            .printAt(ctx, '1/3', 14, textColor, 403, oneThirdAngle)
            .drawHand(ctx, twoThirdAngle, 413, 443, 3.5, handColor)
            .printAt(ctx, '2/3', 14, textColor, 403, twoThirdAngle)
            .markAlarms(ctx, 391)
            .drawArrow(ctx, hourAngle, 479, 41, 59, handColor)
            .drawArrow(ctx, hourAngle, 479, 41, 56, handAccentColor)
            .drawCircle(ctx, 482, handColor, 9)
            .print(ctx, displayTime, 250, textColor, -27)
            .print(ctx, statusText, 31, textColor, 109)
            .print(ctx, nextText, 156, textColor, 223)
            .updateTitle(ctx, `AzanClock • ${currentName} • ${t('clock.next')}: ${nextName} ${t('common.at')} ${nextVakit.time} ${t('clock.in')} ${nextText} • ${locationSettings.address}`)
            .clockDate(ctx, clockDate, clockHijriDate)
            .prayerTimes(ctx)

        if (currentArcVakit.name !== 'Duhaend')
            sac.print(ctx, currentArcName, 37, textColor, -191);

    })

    const prayerName = (name) => t('prayers.' + name);

    const getClockDate = () => {
        if (language === 'pt-BR') {
            return normalizeDigits(new Date().toLocaleDateString('pt-BR', {
                timeZone: locationSettings.timeZoneID,
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }));
        }

        if (!isRtl)
            return todaysDate;

        return normalizeDigits(new Date().toLocaleDateString('ar-EG', {
            timeZone: locationSettings.timeZoneID,
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }));
    }

    const getClockHijriDate = () => {
        if (language === 'pt-BR') {
            return normalizeDigits(new Date().toLocaleDateString('pt-BR-u-ca-islamic-umalqura', {
                timeZone: locationSettings.timeZoneID,
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }));
        }

        if (!isRtl)
            return hijriDate;

        return normalizeDigits(new Date().toLocaleDateString('ar-SA-u-ca-islamic-umalqura', {
            timeZone: locationSettings.timeZoneID,
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).replace('هـ', '').trim() + ' هـ');
    }

    const getDashboardDate = () => {
        const now = new Date();
        const locale = language === 'ar' ? 'ar-EG' : language;
        const hijriLocale = language === 'ar' ? 'ar-SA-u-ca-islamic-umalqura' : `${language}-u-ca-islamic-umalqura`;

        return {
            gregorian: normalizeDigits(now.toLocaleDateString(locale, {
                timeZone: locationSettings.timeZoneID,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            })),
            hijri: normalizeDigits(now.toLocaleDateString(hijriLocale, {
                timeZone: locationSettings.timeZoneID,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).replace('هـ', '').trim())
        };
    }

    const normalizeDigits = (value) => String(value)
        .replace(/[٠-٩]/g, digit => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
        .replace(/[۰-۹]/g, digit => '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit));

    const getIslamicParts = (date) => {
        const parts = new Intl.DateTimeFormat('en-SA-u-ca-islamic-umalqura', {
            timeZone: locationSettings.timeZoneID,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        }).formatToParts(date);

        return {
            day: Number(parts.find(p => p.type === 'day')?.value),
            month: Number(parts.find(p => p.type === 'month')?.value),
            year: Number(parts.find(p => p.type === 'year')?.value)
        };
    }

    const daysUntilIslamicDate = (targetMonth, targetDay) => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        for (let days = 0; days <= 420; days++) {
            const candidate = new Date(start);
            candidate.setDate(start.getDate() + days);
            const parts = getIslamicParts(candidate);

            if (parts.month === targetMonth && parts.day === targetDay) {
                return days;
            }
        }

        return null;
    }

    const renderDashboard = () => {
        const dates = getDashboardDate();
        const dailyPrayerNames = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
        const dailyPrayers = dailyPrayerNames
            .map(name => vakits.find(v => v.name === name))
            .filter(Boolean);
        const sunrise = vakits.find(v => v.name === 'Sunrise');
        const currentName = prayerName(currentVakit.name);
        const events = [
            { label: t('dashboard.ramadan'), days: daysUntilIslamicDate(9, 1) },
            { label: t('dashboard.eidFitr'), days: daysUntilIslamicDate(10, 1) },
            { label: t('dashboard.eidAdha'), days: daysUntilIslamicDate(12, 10) }
        ];

        return (
            <div className={'dashboard-display ' + (isRtl ? 'dashboard-rtl' : '')}
                style={{ '--dashboard-bg': background ? `url(${background})` : 'none' }}
                onClick={handleClick}
                onKeyDown={handleDashboardKeyDown}
                role="button"
                tabIndex="0"
                autoFocus>
                <div className='dashboard-panel'>
                    <div className='dashboard-topline'>
                        <div className='dashboard-date-card'>
                            <span>{t('dashboard.gregorian')}</span>
                            <strong>{dates.gregorian}</strong>
                        </div>
                        <div className='dashboard-date-card'>
                            <span>{t('dashboard.hijri')}</span>
                            <strong>{dates.hijri}</strong>
                        </div>
                    </div>

                    <div className='dashboard-main-grid'>
                        <div className='dashboard-card dashboard-current'>
                            <div className='dashboard-eyebrow'>{t('dashboard.now')}</div>
                            <div className='dashboard-live-time'>{displayTime}</div>
                            <div className='dashboard-subline'>{locationSettings.address}</div>
                        </div>

                        <div className='dashboard-card dashboard-window'>
                            <div className='dashboard-eyebrow'>{t('dashboard.currentPrayer')}</div>
                            <div className='dashboard-window-name'>{currentName}</div>
                            <div className='dashboard-window-time'>
                                {format12(currentVakit.time)} - {format12(nextVakit.time)}
                            </div>
                            <div className='dashboard-subline'>
                                {t('clock.elapsed')} {elapsed} · {t('clock.in')} {nextText}
                            </div>
                        </div>
                    </div>

                    <div className='dashboard-section-title'>{t('dashboard.todayPrayers')}</div>
                    <div className='dashboard-prayers'>
                        {dailyPrayers.map(v => (
                            <div className={'dashboard-prayer ' + (v.name === nextVakit.name ? 'is-next' : '')} key={v.name}>
                                <div className='dashboard-prayer-name'>{prayerName(v.name)}</div>
                                <div className='dashboard-prayer-time'>{format12(v.time)}</div>
                            </div>
                        ))}
                    </div>

                    <div className='dashboard-night'>
                        <div>
                            <div>{prayerName('Sunrise')}</div>
                            <strong>{sunrise ? format12(sunrise.time) : '-'}</strong>
                        </div>
                        <div>
                            <div>{t('settings.midnight')}</div>
                            <strong>{format12(midnightTime)}</strong>
                        </div>
                        <div>
                            <div>{t('dashboard.lastThird')}</div>
                            <strong>{format12(twoThirdTime)}</strong>
                        </div>
                    </div>

                    <div className='dashboard-events'>
                        {events.map(event => (
                            <div className='dashboard-event-card' key={event.label}>
                                <span>{event.label}</span>
                                <strong>{event.days ?? '-'}</strong>
                                <small>{t('dashboard.daysLeft')}</small>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const sac = {
        clearCanvas: (ctx) => {
            ctx.save();
            ctx.translate(0, 0);
            ctx.clearRect(0, 0, size, size);
            ctx.restore();
            return sac;
        },
        drawHand: (ctx, angle, from, to, lineWidth, color) => {
            ctx.save();
            ctx.translate(size / 2, size / 2);
            ctx.beginPath();
            ctx.rotate(angle);
            ctx.moveTo(from, 0);
            ctx.lineTo(to, 0);
            ctx.lineWidth = lineWidth;
            ctx.strokeStyle = color;
            ctx.lineCap = "round";
            ctx.stroke();
            ctx.restore();
            return sac;
        },
        fillCircle: (ctx, r, x, y, color, opacity) => {
            if (dim === 1)
                return sac;
            ctx.save();
            ctx.translate(size / 2, size / 2);
            if (opacity)
                ctx.globalAlpha = opacity;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.restore();
            return sac;
        },
        print: (ctx, text, textSize, color, y) => {
            ctx.save();
            ctx.translate(size / 2, size / 2);
            ctx.font = 'bold ' + Math.floor(textSize) + 'px Arial';
            ctx.fillStyle = color;
            ctx.textBaseline = "middle";
            ctx.textAlign = 'center';
            ctx.direction = isRtl ? 'rtl' : 'ltr';
            ctx.fillText(text, 0, y);
            ctx.restore();
            return sac;
        },
        clockDate: (ctx, dateText, hijriText) => {
            if (isRtl) {
                sac.print(ctx, dateText, 39, textColor, -262);
                sac.print(ctx, hijriText, 34, textColor, -210);
            }
            else {
                sac.arcText(ctx, 'top', dateText, 45, 337, textColor);
                sac.arcText(ctx, 'top', hijriText, 39, 265, textColor);
            }
            return sac;
        },
        prayerTimes: (ctx) => {
            if (!isRtl) {
                sac.arcText(ctx, 'bottom', '#vakits#', 31, 377, textColor);
                return sac;
            }

            const prayerTexts = vakits.map(v => `${prayerName(v.name)} ${format12(v.time)}`);
            sac.print(ctx, prayerTexts.slice(0, 3).join(' · '), 28, textColor, 328);
            sac.print(ctx, prayerTexts.slice(3).join(' · '), 28, textColor, 372);
            return sac;
        },
        printAt(ctx, text, textSize, color, r, angle) {
            if (dim === 1)
                return sac;

            ctx.save();
            ctx.translate(size / 2, size / 2);
            ctx.textBaseline = "middle";
            ctx.fillStyle = color;
            ctx.textAlign = "center";
            ctx.font = textSize + "px Arial";
            let ang = angle - Math.PI / 2;
            ctx.rotate(ang);
            ctx.translate(0, r);
            ctx.rotate(-ang);
            ctx.fillText(text, 0, 0);
            ctx.restore();
            return sac;
        },
        updateTitle(ctx, title) {
            document.title = title;
            return sac;
        },
        drawArrow: (ctx, angle, x, width, height, color) => {
            ctx.save();
            ctx.translate(size / 2, size / 2);

            if (dim === 1) {
                width = width / 2.5;
                height = height / 2.5;
                x = x / 1.065;
            }

            ctx.rotate(angle);
            ctx.beginPath();
            ctx.moveTo(x, -width);
            ctx.lineTo(x, width);
            ctx.lineTo(x - height, 0);
            ctx.fillStyle = (dim === 1 ? silver : color);
            ctx.fill();
            ctx.restore();
            return sac;
        },
        drawIndicator: (ctx, r, angle, color) => {
            ctx.save();
            ctx.translate(size / 2, size / 2);
            ctx.rotate(angle);
            ctx.lineWidth = 2;
            ctx.strokeStyle = handColor;
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.arc(r * 1.076, 0, 9, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.stroke();
            ctx.restore();

        },
        markAlarms: (ctx, r) => {
            alarmSettings.forEach((a) => {
                if ((a.frequency === 'E') || (a.frequency === 'W' && isWeekDay))
                    sac.drawIndicator(ctx, r, a.angle, 'red')
            });
            naflAlarmSettings.forEach((a) => {
                sac.drawIndicator(ctx, r, a.angle, 'yellowgreen')
            });
            return sac;
        },
        drawNumbers24: (ctx, r, fontSize, color) => {
            if (dim === 1)
                return sac;

            let p;
            for (let n = 0; n < 24; n++) {
                ctx.save();
                ctx.translate(size / 2, size / 2);
                ctx.textBaseline = "middle";
                ctx.fillStyle = color;
                ctx.textAlign = "center";
                ctx.font = 'bold ' + fontSize + "px Arial";
                let ang = n * Math.PI / 12;
                ctx.rotate(ang);
                ctx.translate(0, r); /* move the cursor */
                ctx.rotate(-ang);
                if (n === 0)
                    p = formatHourLabel(12, t('common.am'));
                else if (n === 12)
                    p = formatHourLabel(12, t('common.pm'));
                else if (n < 13)
                    p = formatHourLabel(n, t('common.am'));
                else
                    p = formatHourLabel(n - 12, t('common.pm'));
                ctx.fillText(p, 0, 0);
                ctx.restore();
            }
            for (let m = 0; m < 144; m++) {
                ctx.save();
                ctx.translate(size / 2, size / 2);
                ctx.textBaseline = "middle";
                ctx.fillStyle = color;
                ctx.textAlign = "center";
                let ang = m * Math.PI / 72;
                ctx.rotate(ang);
                ctx.translate(0, r * 0.985);
                if (m % 6 === 0) {
                    /*
                    ctx.font = r * 0.051 + "px Arial";
                    ctx.fillText("|", 0, 0);
                    */
                }
                else {
                    ctx.font = r * 0.05 + "px Arial";
                    ctx.fillText(".", 0, 0);
                }
                ctx.restore();
            }

            return sac;

        },
        drawArcs: (ctx, r, arcWidth) => {

            let borderPadding = Math.PI / 450;
            for (let i = 0; i < arcVakits.length; i++) {
                ctx.save();
                ctx.translate(size / 2, size / 2);
                ctx.beginPath();

                if (currentArcVakit.index === i) {
                    ctx.strokeStyle = (dim === 1 ? 'gray' : arcVakits[i].color);
                    ctx.lineWidth = arcWidth * 0.41;
                    ctx.globalAlpha = 1;
                }
                else {
                    ctx.strokeStyle = (dim === 1 ? 'gray' : arcVakits[i].color);
                    ctx.lineWidth = arcWidth * 0.21;
                    ctx.globalAlpha = 0.67;
                }
                ctx.arc(0, 0, r, arcVakits[i].startAngle24(), arcVakits[i].endAngle24() - borderPadding, false);
                ctx.stroke();
                ctx.restore();
            }
            return sac;
        },
        drawCircle: (ctx, r, color, lineWidth, opacity) => {
            ctx.save();
            ctx.translate(size / 2, size / 2);
            if (opacity)
                ctx.globalAlpha = opacity;
            ctx.beginPath();
            ctx.arc(0, 0, r, 0, Math.PI * 2);
            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.stroke();
            ctx.restore();
            return sac;
        },
        arcText: (ctx, mode, text, fontSize, distanceFromCenter, color) => {

            if (text === '#vakits#') {
                text = '';
                for (let v in vakits) {
                    text += prayerName(vakits[v].name) + ' ' + format12(vakits[v].time);
                    if (v * 1 !== (vakits.length - 1) * 1)
                        text += ' · ';
                }
            }

            text = text.replace(/,/g, '')

            let startAngle = 0;
            ctx.font = 'bold ' + fontSize + 'px Arial';

            ctx.fillStyle = color;
            if (mode === 'top') {
                startAngle = -ctx.measureText(text).width / (2 * distanceFromCenter);
            }
            else {
                startAngle = ctx.measureText(text).width / (2 * distanceFromCenter);
            }

            let charWidth = {}
            for (var j = 0; j < text.length; j++) {
                charWidth[text[j]] = ctx.measureText(text[j]).width;
            }

            var thisSpace = 0;
            for (var i = 0; i < text.length; i++) {
                thisSpace += charWidth[text[i]] / distanceFromCenter;
                ctx.save();

                if (text[i] === '·')
                    ctx.fillStyle = 'yellow';

                ctx.translate(size / 2, size / 2);
                ctx.textAlign = "right";
                if (mode === 'top') {
                    ctx.rotate(startAngle + thisSpace);
                    ctx.fillText(text[i], 0, -distanceFromCenter);
                }
                else {
                    ctx.rotate(startAngle - thisSpace);
                    ctx.fillText(text[i], 0, distanceFromCenter);
                }

                ctx.restore();
            }
            return sac;
        }
    }

    const formatHourLabel = (hour, period) => isRtl ? `${hour} ${period}` : `${hour}${period}`;

    const updateBackground = (bg) => {
        document.body.style.setProperty('--clock-bg-brightness', theme.backgroundBrightness);
        document.body.style.setProperty('--clock-vignette-opacity', theme.vignette);
        if (bg.length > 0) {
            document.body.style.backgroundImage = 'url(' + bg + ')';
            document.body.style.backgroundSize = theme.backgroundSize;
            document.body.style.backgroundRepeat = 'no-repeat';
        }
        else {
            document.body.style.backgroundImage = null;
            document.body.style.backgroundSize = null;
            document.body.style.backgroundRepeat = null;
            document.body.style.backgroundPosition = null;
        }
    }

    if (deviceSettings.layout === 'dashboard') {
        return renderDashboard();
    }

    return (
        <div className='d-flex flex-row h-100 align-items-center justify-content-center'
            style={{ overflow: 'hidden' }}>
            <div ref={driftRef}>
                <div ref={dragWrapperRef}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onClick={handleClick}
                    style={{ touchAction: 'none', cursor: 'grab' }}>
                    <canvas id="clockCanvas" className="img-fluid"
                        style={{ opacity: clockOpacity, transform: deviceSettings.zoomedIn === 'Y' ? 'scale(2.2) translateY(-3%)' : 'none' }}
                        width={size} height={size} ref={canvasRef} ></canvas>
                </div>
            </div>
        </div >
    );
}
